/**
 * Mystery Offer popup — email capture + free gift.
 * Markup/settings: sections/mystery-offer-popup.liquid (edit in the theme customizer).
 *
 * Flow: validate email -> save it to Shopify (real {% form 'customer' %} submitted into a hidden iframe,
 *       so Shopify's spam protection works and the visitor never leaves the page)
 *       -> (only on success) check cart -> add gift once via /cart/add.js
 *       -> apply discount code via /cart/update.js and confirm Shopify accepted it -> refresh theme cart UI.
 *
 * The discount code (default MYSTERYGIFT) is a real Shopify discount, so checkout honours it.
 * localStorage/sessionStorage here are UX only; abuse protection is the discount's
 * "once per customer" limit at checkout.
 */

// ---------------------------------------------------------------------------
// Behaviour constants (per-store content/timing lives in the section settings)
// ---------------------------------------------------------------------------
const CONFIG = {
  /** Set after a successful claim; popup never shows again in this browser. */
  CLAIMED_KEY: 'tomatotes:mystery-offer:claimed',
  /** Set when the visitor closes the popup; hides it for the rest of the session. */
  DISMISSED_KEY: 'tomatotes:mystery-offer:dismissed',
  /** Set when the visitor hides the 'Mystery offer' button; hides it for the rest of the session. */
  TEASER_HIDDEN_KEY: 'tomatotes:mystery-offer:teaser-hidden',
  /** Fallback delay if the section setting is missing (seconds). */
  DEFAULT_DELAY_SECONDS: 2,
  /** Quantity added by the popup — never more than 1. */
  GIFT_QUANTITY: 1,
  /** Tags written on the Shopify customer so you can segment these signups. */
  CUSTOMER_TAGS: 'newsletter,mystery-offer-popup',
  /** Basic email check before we hit Shopify (Shopify validates again server-side). */
  EMAIL_PATTERN: /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/,
  REQUEST_TIMEOUT_MS: 15000,
  /** How long to wait for Shopify to confirm the signup (not counting a visible human check). */
  SIGNUP_TIMEOUT_MS: 20000,
  /** How often to check the hidden iframe's address for Shopify's result. */
  SIGNUP_POLL_MS: 120,
};

const storage = {
  get(store, key) {
    try { return window[store].getItem(key); } catch { return null; }
  },
  set(store, key, value) {
    try { window[store].setItem(key, value); } catch { /* private mode etc. */ }
  },
};

/** Load the theme's standard-events module if present; the popup still works without it. */
async function loadStandardEvents() {
  try {
    return await import('@shopify/events');
  } catch {
    return null;
  }
}

function fetchWithTimeout(url, options = {}) {
  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), CONFIG.REQUEST_TIMEOUT_MS);
  return fetch(url, { credentials: 'same-origin', ...options, signal: controller.signal }).finally(() =>
    clearTimeout(timer)
  );
}

class MysteryOfferPopup extends HTMLElement {
  #busy = false;
  #lastFocused = null;
  #autoCloseTimer = null;
  #showTimer = null;
  #allowResubmit = false;
  /** Set while waiting for the signup result; lets either signup path report back. */
  #finishSignup = null;

  connectedCallback() {
    // The signup result page loads inside our hidden iframe; never run the popup in there.
    if (window.self !== window.top) return;

    this.form = this.querySelector('[data-mop-form]');
    this.frame = this.querySelector('[data-mop-frame]');
    this.submitButton = this.querySelector('[data-mop-submit]');
    this.errorEl = this.querySelector('[data-mop-error]');
    this.emailInput = this.querySelector('input[name="contact[email]"]');
    this.teaser = document.querySelector(`[data-mop-teaser-for="${this.id}"]`);
    this.teaser?.querySelector('[data-mop-teaser-open]')?.addEventListener('click', () => this.open());
    this.teaser?.querySelector('[data-mop-teaser-close]')?.addEventListener('click', () => {
      if (!this.isDesignMode) storage.set('sessionStorage', CONFIG.TEASER_HIDDEN_KEY, '1');
      this.#setTeaser(false);
    });

    // Shopify's spam-protection script finishes by calling form.submit() once it has its token.
    // Send that final submit with fetch instead, so we can read Shopify's answer directly.
    if (this.form) {
      const nativeSubmit = this.form.submit.bind(this.form);
      this.form.submit = () => (this.#finishSignup ? this.#postSignup() : nativeSubmit());
    }

    // Capture phase on the host so validation runs before Shopify's spam-protection script sees the submit.
    this.addEventListener('submit', this.#onSubmit, true);
    this.querySelectorAll('[data-mop-close]').forEach((el) => el.addEventListener('click', this.#onCloseClick));
    this.addEventListener('keydown', this.#onKeydown);

    if (this.isDesignMode) {
      // Theme editor: open when the section is selected, close when deselected. Ignore stored flags.
      document.addEventListener('shopify:section:select', this.#onEditorSelect);
      document.addEventListener('shopify:section:deselect', this.#onEditorDeselect);
      this.#setTeaser(true);
      return;
    }

    // Already claimed in this browser: show nothing.
    if (storage.get('localStorage', CONFIG.CLAIMED_KEY)) return;

    this.#setTeaser(!storage.get('sessionStorage', CONFIG.TEASER_HIDDEN_KEY));

    // Opens only when the button is clicked, unless "Also open automatically" is on.
    if (this.dataset.autoOpen !== 'true' || this.#shouldSuppress()) return;

    const delay = Number.parseFloat(this.dataset.delaySeconds);
    const seconds = Number.isFinite(delay) ? delay : CONFIG.DEFAULT_DELAY_SECONDS;
    this.#showTimer = setTimeout(() => this.open(), Math.max(0, seconds) * 1000);
  }

  disconnectedCallback() {
    clearTimeout(this.#showTimer);
    clearTimeout(this.#autoCloseTimer);
    document.removeEventListener('shopify:section:select', this.#onEditorSelect);
    document.removeEventListener('shopify:section:deselect', this.#onEditorDeselect);
    document.documentElement.classList.remove('mop-scroll-lock');
  }

  get isDesignMode() {
    return this.dataset.designMode === 'true';
  }

  get variantId() {
    return Number.parseInt(this.dataset.variantId, 10);
  }

  #setTeaser(visible) {
    if (this.teaser) this.teaser.hidden = !visible;
  }

  #teaserAllowed() {
    return (
      this.isDesignMode ||
      (!storage.get('localStorage', CONFIG.CLAIMED_KEY) && !storage.get('sessionStorage', CONFIG.TEASER_HIDDEN_KEY))
    );
  }

  #shouldSuppress() {
    return Boolean(
      storage.get('localStorage', CONFIG.CLAIMED_KEY) || storage.get('sessionStorage', CONFIG.DISMISSED_KEY)
    );
  }

  // -------------------------------------------------------------------------
  // Open / close
  // -------------------------------------------------------------------------
  open() {
    if (!this.hidden) return;
    this.#lastFocused = document.activeElement;
    this.#setTeaser(false);
    this.hidden = false;
    document.documentElement.classList.add('mop-scroll-lock');
    requestAnimationFrame(() => {
      this.classList.add('is-visible');
      // Focus the first field on desktop only; on mobile this would pop the keyboard immediately.
      if (window.matchMedia('(min-width: 750px)').matches) this.emailInput?.focus({ preventScroll: true });
      else this.querySelector('.mop__close')?.focus({ preventScroll: true });
    });
  }

  close({ remember = true } = {}) {
    if (this.hidden) return;
    clearTimeout(this.#autoCloseTimer);
    if (remember && !this.isDesignMode) storage.set('sessionStorage', CONFIG.DISMISSED_KEY, '1');
    this.classList.remove('is-visible');
    document.documentElement.classList.remove('mop-scroll-lock');
    setTimeout(() => {
      this.hidden = true;
      this.#setTeaser(this.#teaserAllowed());
    }, 250);
    if (this.#lastFocused instanceof HTMLElement) this.#lastFocused.focus({ preventScroll: true });
  }

  #onCloseClick = (event) => {
    event.preventDefault();
    if (this.#busy) return; // don't let a mid-request close leave a half-finished claim
    this.close();
  };

  #onKeydown = (event) => {
    if (event.key === 'Escape' && !this.#busy) {
      this.close();
      return;
    }
    if (event.key === 'Tab') this.#trapFocus(event);
  };

  #trapFocus(event) {
    const focusable = [...this.querySelectorAll('button, input, a[href]')].filter(
      (el) => !el.disabled && el.offsetParent !== null
    );
    if (!focusable.length) return;
    const first = focusable[0];
    const last = focusable[focusable.length - 1];
    if (event.shiftKey && document.activeElement === first) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && document.activeElement === last) {
      event.preventDefault();
      first.focus();
    }
  }

  #onEditorSelect = (event) => {
    if (event.detail?.sectionId === this.dataset.sectionId) {
      this.#showState('form');
      this.open();
    }
  };

  #onEditorDeselect = (event) => {
    if (event.detail?.sectionId === this.dataset.sectionId) this.close({ remember: false });
  };

  // -------------------------------------------------------------------------
  // Submit
  // -------------------------------------------------------------------------
  #onSubmit = (event) => {
    if (event.target !== this.form) return;

    const block = () => {
      event.preventDefault();
      event.stopImmediatePropagation();
    };

    if (this.#busy) {
      // Shopify's spam-protection script may re-submit the form once it has its token; let that through.
      if (this.#allowResubmit) {
        this.#allowResubmit = false;
        return;
      }
      block(); // double-click / double-submit guard
      return;
    }

    this.#clearError();
    const email = this.emailInput.value.trim();

    if (!CONFIG.EMAIL_PATTERN.test(email)) {
      block();
      this.#showError(this.dataset.msgInvalidEmail, this.emailInput);
      return;
    }

    // Valid: let the real Shopify form submit into the hidden iframe (no page change).
    this.#setBusy(true);
    setTimeout(() => {
      // If another script (Shopify's hCaptcha) paused this submit, it will submit again itself.
      if (event.defaultPrevented) this.#allowResubmit = true;
    }, 0);

    this.#finishClaim();
  };

  async #finishClaim() {
    // 1) Signup. Gift is only attempted if this succeeds.
    let signup;
    try {
      signup = await this.#waitForSignup();
    } catch (error) {
      console.warn('[mystery-offer] signup failed', error);
      signup = { ok: false };
    }
    this.#allowResubmit = false;
    this.#hideChallenge();

    if (!signup.ok) {
      this.#setBusy(false);
      this.#showError(signup.message || this.dataset.msgSignupError, signup.field === 'email' ? this.emailInput : null);
      return;
    }

    // Signup done — remember it now so the popup won't reappear even if the gift step fails.
    storage.set('localStorage', CONFIG.CLAIMED_KEY, new Date().toISOString());

    // 2) Gift + discount.
    let result;
    try {
      result = await this.#addGiftAndDiscount();
    } catch (error) {
      console.warn('[mystery-offer] gift step failed', error);
      this.#setBusy(false);
      this.#showError(this.dataset.msgGiftError);
      this.submitButton.disabled = true; // signup is done; don't re-submit
      return;
    }

    this.#setBusy(false);
    if (!result.discountApplied) {
      // Gift is in the cart but Shopify didn't accept the code: say so instead of claiming it's free.
      const text = this.querySelector('[data-mop-success-text]');
      if (text && this.dataset.msgDiscountError) text.textContent = this.dataset.msgDiscountError;
    }
    this.#showState('success');
    this.#scheduleAutoClose();
  }

  /**
   * Waits for Shopify's answer to the signup form, which was submitted into the hidden iframe.
   * Shopify stores the email as a customer contact with marketing consent (no login/account).
   *   - URL gets ?customer_posted=true      -> saved (also the case for an email that already exists)
   *   - URL is Shopify's /challenge page    -> show the human check inside the popup and keep waiting
   *   - any other page                       -> read it for an "already taken" / "invalid" message
   */
  #waitForSignup() {
    const frame = this.frame;
    if (!frame) return Promise.resolve({ ok: false });

    return new Promise((resolve) => {
      const startedAt = Date.now();
      let challengeShown = false;
      let done = false;

      const readHref = () => {
        try {
          return frame.contentWindow?.location?.href || '';
        } catch {
          return '';
        }
      };

      const finish = (result) => {
        if (done) return;
        done = true;
        clearInterval(timer);
        frame.removeEventListener('load', onLoad);
        try {
          frame.contentWindow?.stop();
        } catch {
          /* ignore */
        }
        console.info('[mystery-offer] signup result', result.ok ? 'saved' : 'failed', result.via || '', readHref());
        this.#finishSignup = null;
        frame.src = 'about:blank'; // clean slate for any retry
        resolve(result);
      };
      this.#finishSignup = finish;

      const check = (pageLoaded) => {
        const href = readHref();

        // Shopify pages refuse to display inside a frame, so after the form posts the frame usually
        // becomes unreadable. The request has still reached Shopify and been processed at that point.
        if (!href && pageLoaded) {
          finish({ ok: true, via: 'frame-response' });
          return;
        }
        if (!href || href === 'about:blank') return;

        if (href.includes('customer_posted=true')) {
          finish({ ok: true, via: 'frame' });
          return;
        }
        if (href.includes('/challenge')) {
          if (!challengeShown) {
            challengeShown = true;
            this.#showChallenge();
          }
          return;
        }
        if (!pageLoaded) return;

        let text = '';
        try {
          text = frame.contentDocument?.body?.innerText || '';
        } catch {
          /* ignore */
        }
        // An email that already exists in Shopify is not an error: the visitor still gets the gift.
        if (/has already been taken|already (been )?(registered|subscribed)|already exists/i.test(text)) {
          finish({ ok: true });
        } else if (/email[^\n]{0,60}invalid/i.test(text)) {
          finish({ ok: false, message: this.dataset.msgInvalidEmail, field: 'email' });
        } else {
          finish({ ok: false });
        }
      };

      const onLoad = () => check(true);
      frame.addEventListener('load', onLoad);

      const timer = setInterval(() => {
        check(false);
        if (!challengeShown && Date.now() - startedAt > CONFIG.SIGNUP_TIMEOUT_MS) finish({ ok: false });
      }, CONFIG.SIGNUP_POLL_MS);
    });
  }

  /** Posts the (token-carrying) signup form with fetch and reports Shopify's answer. */
  async #postSignup() {
    const finish = this.#finishSignup;
    if (!finish) return;
    try {
      const response = await fetchWithTimeout(this.form.action, {
        method: 'POST',
        body: new FormData(this.form),
        headers: { Accept: 'text/html' },
      });
      const url = response.url || '';
      if (url.includes('customer_posted=true')) {
        finish({ ok: true, via: 'fetch' });
        return;
      }
      if (url.includes('/challenge')) {
        finish({ ok: false, via: 'fetch-challenge' });
        return;
      }
      const html = await response.text().catch(() => '');
      if (/customer_posted=true|has already been taken|already (been )?(registered|subscribed)|already exists/i.test(html)) {
        finish({ ok: true, via: 'fetch' });
      } else if (/email[^<]{0,60}invalid/i.test(html)) {
        finish({ ok: false, via: 'fetch', message: this.dataset.msgInvalidEmail, field: 'email' });
      } else {
        finish({ ok: false, via: 'fetch' });
      }
    } catch (error) {
      console.warn('[mystery-offer] signup request failed', error);
      finish({ ok: false, via: 'fetch-error' });
    }
  }

  #showChallenge() {
    this.frame?.classList.add('is-challenge');
    this.frame?.setAttribute('tabindex', '0');
    if (this.dataset.msgChallenge) {
      this.errorEl.textContent = this.dataset.msgChallenge;
      this.errorEl.hidden = false;
    }
  }

  #hideChallenge() {
    if (!this.frame) return;
    this.frame.classList.remove('is-challenge');
    this.frame.setAttribute('tabindex', '-1');
  }

  async #getCart() {
    const response = await fetchWithTimeout(`${this.#routes().cart_url}.js`, {
      headers: { Accept: 'application/json' },
    });
    if (!response.ok) throw new Error(`cart fetch ${response.status}`);
    return response.json();
  }

  #routes() {
    const r = window.Theme?.routes || {};
    return {
      cart_url: r.cart_url || '/cart',
      cart_add_url: r.cart_add_url || '/cart/add.js',
      cart_update_url: r.cart_update_url ? `${r.cart_update_url}.js` : '/cart/update.js',
    };
  }

  #cartSectionIds() {
    return [...document.querySelectorAll('cart-items-component')]
      .map((el) => el.dataset.sectionId)
      .filter(Boolean);
  }

  async #addGiftAndDiscount() {
    const variantId = this.variantId;
    if (!Number.isFinite(variantId)) throw new Error('Gift variant ID is not set');

    const events = await loadStandardEvents();
    const CartLinesUpdateEvent = events?.CartLinesUpdateEvent;
    const deferred = CartLinesUpdateEvent?.createPromise?.();

    // Check first: never add a second gift.
    const cartBefore = await this.#getCart();
    const alreadyInCart = cartBefore.items?.some((item) => Number(item.variant_id) === variantId);

    if (deferred) {
      // Same event the theme's product form fires: updates the cart bubble, re-renders the drawer,
      // and opens it when the theme's "Auto-open cart drawer" setting is on.
      document.dispatchEvent(
        new CartLinesUpdateEvent({
          action: alreadyInCart ? 'update' : 'add',
          context: 'product',
          lines: [{ merchandiseId: String(variantId), quantity: CONFIG.GIFT_QUANTITY }],
          promise: deferred.promise,
        })
      );
    }

    try {
      let sections;
      if (!alreadyInCart) {
        const addResponse = await fetchWithTimeout(this.#routes().cart_add_url, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({
            items: [{ id: variantId, quantity: CONFIG.GIFT_QUANTITY }],
            sections: this.#cartSectionIds().join(','),
          }),
        });
        const addData = await addResponse.json().catch(() => ({}));
        if (!addResponse.ok || addData.status) {
          throw new Error(addData.description || addData.message || `add ${addResponse.status}`);
        }
        sections = addData.sections;
      }

      const discountApplied = await this.#applyDiscount(cartBefore);

      const cartAfter = await this.#refreshThemeCart();
      deferred?.resolve({
        cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cartAfter),
        detail: {
          items: cartAfter.items,
          source: 'mystery-offer-popup',
          sourceId: this.id,
          itemCount: CONFIG.GIFT_QUANTITY,
          sections,
          didError: false,
        },
      });
      return { discountApplied };
    } catch (error) {
      // Resolve with the real cart so the bubble/drawer stay accurate, then surface the error.
      try {
        const cart = await this.#getCart();
        deferred?.resolve({
          cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
          detail: { items: cart.items, source: 'mystery-offer-popup', didError: true, itemCount: 0 },
        });
      } catch {
        deferred?.reject(error);
      }
      throw error;
    }
  }

  /**
   * Applies the existing gift discount code with Shopify's Cart AJAX API (/cart/update.js `discount`),
   * keeping any codes the customer already had. Returns true only if Shopify reports the code as
   * applicable to this cart, so we never claim "free" when it isn't.
   */
  async #applyDiscount(cart) {
    const code = (this.dataset.discountCode || '').trim();
    if (!code) return false;

    const isApplied = (c) =>
      (c?.discount_codes || []).some((d) => d.code?.toUpperCase() === code.toUpperCase() && d.applicable !== false);

    if (isApplied(cart)) return true;

    const existing = (cart.discount_codes || [])
      .map((d) => d.code)
      .filter((c) => c && c.toUpperCase() !== code.toUpperCase());
    const codes = [...existing, code].join(',');

    try {
      const response = await fetchWithTimeout(this.#routes().cart_update_url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ discount: codes }),
      });
      if (response.ok && isApplied(await response.json().catch(() => null))) return true;
    } catch {
      /* try the fallback below */
    }

    // Fallback: Shopify's /discount/CODE link stores the code for the cart and checkout.
    await fetchWithTimeout(`/discount/${encodeURIComponent(code)}`, { method: 'GET' }).catch(() => {});
    try {
      return isApplied(await this.#getCart());
    } catch {
      return false;
    }
  }

  /** Re-renders the theme cart drawer the same way the theme's product form does. */
  async #refreshThemeCart() {
    const cartItems = document.querySelector('cart-items-component');
    if (cartItems) {
      try {
        await customElements.whenDefined('cart-items-component');
        if (typeof cartItems.fetchCartData === 'function') {
          const data = await cartItems.fetchCartData();
          if (data?.items) return data;
        }
      } catch {
        /* fall back to cart.js */
      }
    }
    return this.#getCart();
  }

  // -------------------------------------------------------------------------
  // UI helpers
  // -------------------------------------------------------------------------
  #setBusy(busy) {
    this.#busy = busy;
    this.submitButton.disabled = busy;
    this.submitButton.classList.toggle('is-loading', busy);
    this.submitButton.setAttribute('aria-busy', String(busy));
    this.emailInput.readOnly = busy;
  }

  #showError(message, field = null) {
    this.errorEl.textContent = message || '';
    this.errorEl.hidden = !message;
    this.emailInput.removeAttribute('aria-invalid');
    if (field) {
      field.setAttribute('aria-invalid', 'true');
      field.focus();
    }
  }

  #clearError() {
    this.#showError('');
  }

  #showState(name) {
    this.querySelectorAll('[data-mop-state]').forEach((el) => {
      el.hidden = el.dataset.mopState !== name;
    });
    if (name === 'success') this.querySelector('[data-mop-success-heading]')?.focus({ preventScroll: true });
  }

  #scheduleAutoClose() {
    const seconds = Number.parseFloat(this.dataset.autoCloseSeconds);
    if (!Number.isFinite(seconds) || seconds <= 0) return;
    this.#autoCloseTimer = setTimeout(() => this.close(), seconds * 1000);
  }
}

if (!customElements.get('mystery-offer-popup')) {
  customElements.define('mystery-offer-popup', MysteryOfferPopup);
}
